import { Toolbar } from "client/Components";
import { ReactElement, useMemo } from "react";
import { localize } from "client_legacy/ndaw/culture";
import { useFollowAction } from "./useFollowAction";
import { ReactionsButton } from "client/Features/Reactions/ReactionsButton/ReactionsButton";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { HubDataSource } from "client/Api/Hub/hub";
import { ReactionKind } from "client/Api/Hub/common";
import {
	KnowledgeItemStatisticsModel,
	registerReaction,
	unregisterReaction,
	updateReaction
} from "client/Api/Hub/KnowledgeItems/knowledgeItemViewer";
import { getHubUrlForKnowledgeItem } from "../../KnowledgeItemsPage";

export type KnowledgeItemActionsProps = {
	statistics: KnowledgeItemStatisticsModel;
	onStatisticsRetrieved: (statistics: KnowledgeItemStatisticsModel) => void;
	knowledgeItemId: number;
	areCommentsEnabled: boolean;
};

export const KnowledgeItemActions = (props: KnowledgeItemActionsProps): ReactElement | null => {
	const {
		statistics,
		onStatisticsRetrieved,
		knowledgeItemId,
		areCommentsEnabled
	} = props;

	const sendReaction = (reaction: ReactionKind): Promise<KnowledgeItemStatisticsModel> => {
		if (statistics.currentUserReaction === ReactionKind.NoReaction) {
			return registerReaction(knowledgeItemId, reaction);
		}

		if (reaction === statistics.currentUserReaction) {
			return unregisterReaction(knowledgeItemId);
		}

		return updateReaction(knowledgeItemId, reaction);
	};

	const handleReactionClick = async (reaction: ReactionKind): Promise<void> => {
		try {
			const statistics: KnowledgeItemStatisticsModel = await sendReaction(reaction);

			onStatisticsRetrieved(statistics);
		} catch (e) {
			if (isCancelledError(e)) {
				return;
			}

			throw e;
		}
	};

	const knowledgeItemLink = useMemo(
		(): string => {
			const result: string = getHubUrlForKnowledgeItem({
				source: HubDataSource.KnowledgeItem,
				itemId: knowledgeItemId
			});

			return result;
		},
		[knowledgeItemId]
	);

	const [
		followIcon,
		followText,
		followClickHandler
	] = useFollowAction(
		knowledgeItemId,
		statistics.isCurrentUserFollowingComments,
		onStatisticsRetrieved
	);

	return (
		<Toolbar
			moreButtonKind="secondary"
		>
			<Toolbar.Custom
				responsiveBehavior="always-visible"
				renderNormalMode={() => (
					<ReactionsButton
						userReaction={statistics.currentUserReaction}
						onReactionClick={handleReactionClick}
						kind="secondary"
					/>
				)}
				renderCollapsedMode={() => { throw new Error("Does not support collapse"); }}
			/>

			{areCommentsEnabled && (
				<Toolbar.Button
					kind="secondary"
					trackingId="follow"
					text={localize(followText)}
					icon={followIcon}
					onClick={followClickHandler}
				/>
			)}

			<Toolbar.CopyButton
				value={knowledgeItemLink}
				kind="secondary"
				text={localize("_CopyLink")}
				icon="link"
				trackingId="copy-link"
			/>
		</Toolbar>
	);
};

KnowledgeItemActions.displayName = "KnowledgeItemActions";